<script>
import axios from '@/utils/axios';
import { clientAdmin } from "@/utils/auth";

export default {
  inject: ['setLoading'],
  data() {
    return {
      title: "Reports",
      text: "Inventory",
      text1: "Production Report",
      clientAdmin: clientAdmin(),

      // Client (Lead) filter
      selectedLead: null,
      leads: [],
      leadsLoading: false,
      leadSearchTimer: null,

      // Work Order filter
      selectedWorkOrder: null,
      work_orders: [],
      workOrdersLoading: false,

      // Report data
      productionData: [],
      loading: false,
      hasSearched: false,
    };
  },

  computed: {
    groupedReport() {
      return this.productionData.map(so => {
        const itemMap = {};

        (so.store_dispatch_lists || []).forEach(row => {
          const id = row.item_id;
          if (!itemMap[id]) {
            itemMap[id] = {
              item_id: id,
              name: row.item?.name || 'N/A',
              code: row.item?.code || '-',
              requested: 0,
              dispatched: 0,
            };
          }
          itemMap[id].requested += parseFloat(row.quantity || 0);
        });

        const challanItems = so.delivery_challan?.challan_items || [];
        challanItems.forEach(ci => {
          const id = ci.item_id;
          if (!itemMap[id]) {
            itemMap[id] = {
              item_id: id,
              name: 'N/A',
              code: '-',
              requested: 0,
              dispatched: 0,
            };
          }
          itemMap[id].dispatched += parseFloat(ci.quantity || 0);
        });

        const items = Object.values(itemMap).map(item => ({
          ...item,
          pending: Math.max(0, item.requested - item.dispatched),
        }));

        const totals = items.reduce((acc, i) => {
          acc.requested += i.requested;
          acc.dispatched += i.dispatched;
          acc.pending += i.pending;
          return acc;
        }, { requested: 0, dispatched: 0, pending: 0 });

        return {
          so_number: so.so_number,
          created_at: so.created_at,
          challan_number: so.delivery_challan?.challan_number || null,
          items,
          totals,
        };
      });
    },

    // Overall summary across all SOs (for top cards)
    overallSummary() {
      return this.groupedReport.reduce((acc, so) => {
        acc.requested += so.totals.requested;
        acc.dispatched += so.totals.dispatched;
        acc.pending += so.totals.pending;
        return acc;
      }, { requested: 0, dispatched: 0, pending: 0 });
    },
  },

  methods: {

async exportCSV() {
  if (!this.selectedWorkOrder?.id) return;

  this.setLoading(true);
  try {
    const payload = { export: 'csv', work_order_id: this.selectedWorkOrder.id };
    const query_string = new URLSearchParams(payload).toString();
    const response = await axios.get(`/reports/production?${query_string}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `production-report-${this.selectedWorkOrder.code || this.selectedWorkOrder.id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    this.$swalToast('Failed to export CSV', 'error');
  } finally {
    this.setLoading(false);
  }
},



    onLeadSearch(search) {
      clearTimeout(this.leadSearchTimer);
      if (!search || search.length < 2) {
        this.leads = [];
        return;
      }
      this.leadSearchTimer = setTimeout(() => {
        this.fetchLeads(search);
      }, 400);
    },

    async fetchLeads(search) {
      this.leadsLoading = true;
      try {
        const res = await axios.get('/leads/dropdown', { params: { search_keyword: search } });
        this.leads = res.data.data || [];
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.leadsLoading = false;
      }
    },

    async onLeadSelected(lead) {
      this.selectedWorkOrder = null;
      this.work_orders = [];
      this.productionData = [];
      this.hasSearched = false;

      if (!lead?.id) return;

      this.workOrdersLoading = true;
      try {
        const res = await axios.get('/work-orders/by-party', {
          params: { party_type: 'App\\Models\\Lead', party_id: lead.id }
        });
        this.work_orders = res.data.data || [];
      } catch (error) {
        this.work_orders = [];
        this.$swalToast(error.message, 'error');
      } finally {
        this.workOrdersLoading = false;
      }
    },

    async onWorkOrderSelected(workOrder) {
      this.productionData = [];
      if (!workOrder?.id) return;

      this.loading = true;
      this.hasSearched = true;
      try {
        const res = await axios.get('/reports/production', {
          params: { work_order_id: workOrder.id }
        });
        this.productionData = res.data.data || [];
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    resetFilters() {
      this.selectedLead = null;
      this.selectedWorkOrder = null;
      this.work_orders = [];
      this.productionData = [];
      this.hasSearched = false;
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatQty(val) {
      const num = parseFloat(val || 0);
      return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    },

    completionPct(so) {
      if (!so.totals.requested) return 0;
      return Math.round((so.totals.dispatched / so.totals.requested) * 100);
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
        <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!groupedReport.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
            </button>
        </div>
        </div>

      <!-- Filters -->
      <div class="card border-0 shadow-sm">
        <div class="card-body">
          <div class="row g-3 align-items-end">

            <div class="col-md-4">
              <label class="form-label fw-semibold" style="font-size:12px;color:#475569;">
                <i class="ti ti-user-search me-1 text-primary"></i>Client
              </label>
              <vue-select
                v-model="selectedLead"
                :options="leads"
                :loading="leadsLoading"
                :filterable="false"
                label="name"
                placeholder="Search lead by name..."
                @search="onLeadSearch"
                @update:modelValue="onLeadSelected"
              />
            </div>

            <div class="col-md-4">
                <div class="d-flex align-items-center justify-content-between mb-1">
                    <label class="form-label fw-semibold" style="font-size:12px;color:#475569;">
                        <i class="ti ti-clipboard-list me-1 text-primary"></i>Work Order
                    </label>
                        <small class="text-muted" v-if="!selectedLead" style="font-size:10.5px;">
                        <i class="ti ti-info-circle"></i> Select a client first
                    </small>
                </div>
              <vue-select
                v-model="selectedWorkOrder"
                :options="work_orders"
                label="name"
                :get-option-label="w => `${w.name} (${w.code})`"
                placeholder="Select work order"
                :disabled="!selectedLead || workOrdersLoading"
                @update:modelValue="onWorkOrderSelected"
              />
            </div>

            <div class="col-md-4 d-flex justify-content-md-end">
              <button
                type="button"
                class="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                v-if="selectedLead || selectedWorkOrder"
                @click="resetFilters"
              >
                <i class="ti ti-filter-x"></i> Clear Filters
              </button>
            </div>

          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="card border-0 shadow-sm mt-3">
        <div class="card-body text-center p-5">
          <div class="spinner-border text-primary mb-3" role="status"></div>
          <h6 class="text-muted mb-0">Loading production report...</h6>
        </div>
      </div>

      <!-- No filters selected yet -->
      <div v-else-if="!hasSearched" class="card border-0 shadow-sm mt-3">
        <div class="card-body text-center p-5">
          <div style="width:60px;height:60px;background:#eff6ff;border-radius:14px;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;">
            <i class="ti ti-clipboard-list" style="font-size:26px;color:#3b82f6;"></i>
          </div>
          <h6 class="fw-bold text-dark mb-1">No Report Selected</h6>
          <p class="text-muted mb-0" style="font-size:13px;">Select a Client and Work Order above to view the production report.</p>
        </div>
      </div>

      <!-- No data -->
      <div v-else-if="!groupedReport.length" class="card border-0 shadow-sm mt-3">
        <div class="card-body text-center p-5">
          <div style="width:60px;height:60px;background:#f8fafc;border-radius:14px;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;">
            <i class="ti ti-inbox" style="font-size:26px;color:#94a3b8;"></i>
          </div>
          <h6 class="fw-bold text-dark mb-1">No Data Found</h6>
          <p class="text-muted mb-0" style="font-size:13px;">No Sales Order / Dispatch data found for this Work Order.</p>
        </div>
      </div>

      <!-- Report Content -->
      <div v-else>

        <!-- Overall Summary Cards -->
        <div class="row g-3 mt-1 mb-5">
          <div class="col-md-4 col-6">
            <div class="card mb-0 border-0 shadow-sm">
              <div class="card-body p-3 d-flex align-items-center gap-3">
                <div style="width:42px;height:42px;background:#eff6ff;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                  <i class="ti ti-package" style="color:#3b82f6;font-size:19px;"></i>
                </div>
                <div>
                  <span class="text-muted d-block" style="font-size:11px;">Total Requested</span>
                  <span class="fw-bold text-dark" style="font-size:19px;">{{ formatQty(overallSummary.requested) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 col-6">
            <div class="card mb-0 border-0 shadow-sm">
              <div class="card-body p-3 d-flex align-items-center gap-3">
                <div style="width:42px;height:42px;background:#f0fdf4;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                  <i class="ti ti-truck-delivery" style="color:#16a34a;font-size:19px;"></i>
                </div>
                <div>
                  <span class="text-muted d-block" style="font-size:11px;">Total Dispatched</span>
                  <span class="fw-bold text-success" style="font-size:19px;">{{ formatQty(overallSummary.dispatched) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 col-12">
            <div class="card mb-0 border-0 shadow-sm">
              <div class="card-body p-3 d-flex align-items-center gap-3">
                <div style="width:42px;height:42px;background:#fff0f3;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                  <i class="ti ti-clock-hour-4" style="color:#f43f5e;font-size:19px;"></i>
                </div>
                <div>
                  <span class="text-muted d-block" style="font-size:11px;">Total Pending</span>
                  <span class="fw-bold text-danger" style="font-size:19px;">{{ formatQty(overallSummary.pending) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      <!-- SO Blocks -->
      <div class="row g-3">
      <div class="col-md-4" v-for="so in groupedReport" :key="so.so_number">
        <div class="card mb-0 border-0 shadow-sm h-100 d-flex flex-column">
          <div class="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2"
            style="background:linear-gradient(135deg,#0f172a,#1e3a5f);border-radius:8px 8px 0 0;padding:10px 14px;">
            <div>
              <h6 class="fw-bold mb-0" style="color:#fff;font-size:13px;">
                <i class="ti ti-file-invoice me-1"></i>{{ so.so_number }}
              </h6>
              <small style="color:#93c5fd;font-size:10px;">{{ formatDate(so.created_at) }}</small>
            </div>
            <div class="d-flex flex-column align-items-end gap-1">
              <span style="font-size:9.5px;color:#93c5fd;font-weight:600;">{{ completionPct(so) }}% Dispatched</span>
              <span v-if="so.challan_number" class="badge rounded-pill" style="background:rgba(34,197,94,.18);color:#4ade80;font-size:9px;">
                <i class="ti ti-truck-delivery me-1"></i>{{ so.challan_number }}
              </span>
              <span v-else class="badge rounded-pill" style="background:rgba(244,63,94,.18);color:#fb7185;font-size:9px;">
                <i class="ti ti-alert-circle me-1"></i>Not Dispatched
              </span>
            </div>
          </div>

          <!-- Scrollable items area -->
          <div class="so-card-scroll flex-grow-1">
            <table class="card-table compact-report-table mb-0">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Req</th>
                  <th>Disp</th>
                  <th>Pend</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in so.items" :key="item.item_id">
                  <td>
                    <strong class="text-dark d-block" style="font-size:11.5px;">{{ item.name }}</strong>
                    <span class="text-muted" style="font-size:9.5px;">{{ item.code }}</span>
                  </td>
                  <td><span class="badge bg-primary-subtle text-primary rounded-pill px-2" style="font-size:10.5px;">{{ formatQty(item.requested) }}</span></td>
                  <td><span class="badge bg-success-subtle text-success rounded-pill px-2" style="font-size:10.5px;">{{ formatQty(item.dispatched) }}</span></td>
                  <td>
                    <span class="badge rounded-pill px-2" style="font-size:10.5px;" :class="item.pending > 0 ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'">
                      {{ formatQty(item.pending) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Sticky totals footer (always visible, outside scroll) -->
          <div class="d-flex justify-content-between align-items-center px-3 py-2" style="background:#f8fafc;border-top:1px solid #e2e8f0;">
            <strong class="text-dark" style="font-size:11px;">Total</strong>
            <div class="d-flex gap-3">
              <strong class="text-primary" style="font-size:11px;">{{ formatQty(so.totals.requested) }}</strong>
              <strong class="text-success" style="font-size:11px;">{{ formatQty(so.totals.dispatched) }}</strong>
              <strong class="text-danger" style="font-size:11px;">{{ formatQty(so.totals.pending) }}</strong>
            </div>
          </div>
        </div>
      </div>
      </div>

            </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>

<style scoped>
.table-responsive {
  overflow-y: auto;
}
:deep(.vs__dropdown-toggle) {
  min-height: 38px !important;
}
.compact-report-table th,
.compact-report-table td {
  padding: 6px 8px !important;
  font-size: 11px;
}
.so-card-scroll {
  max-height: 220px;
  overflow-y: auto;
}
.so-card-scroll::-webkit-scrollbar {
  width: 5px;
}
.so-card-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
</style>